// DOM utility helpers. No state, no api. Only depends on the DOM.

export function appRoot() {
  return document.getElementById("app");
}

// The app shell lays out at the device's width (src/js/lib/app-layout.js:
// a sidebar, a sidebar over the page, a tab bar, down to 360). Until every
// screen worked at 360 a phone laid it out at 1024px and scaled it to fit;
// that went with docs/designs/responsive-web.md's last step. The shell asks
// for viewport-fit=cover, reaching under a notch and the home indicator,
// because it pads itself clear of both (env(safe-area-inset-*)); the
// marketing landing and the anonymous screens keep the plain viewport, as
// they don't.
//
// Driven off what actually rendered rather than paired set/restore calls at each
// screen: index.html serves both the marketing landing and the app, and with six
// callers of renderAppShell plus the anon screens, one missed call would strand
// the landing page with the wrong viewport.
const APP_VIEWPORT = "width=device-width, initial-scale=1.0, viewport-fit=cover";
const RESPONSIVE_VIEWPORT = "width=device-width, initial-scale=1.0";

export function syncViewportToShell() {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;
  const isShell = Boolean(document.querySelector(".app-shell-page"));
  const next = isShell ? APP_VIEWPORT : RESPONSIVE_VIEWPORT;
  // Only write on a real change — reassigning the viewport forces Safari to
  // re-layout, which visibly jumps the page mid-navigation.
  if (meta.getAttribute("content") !== next) meta.setAttribute("content", next);
}

export function isLoadingButton(button) {
  return button instanceof HTMLButtonElement && button.classList.contains("ar-btn");
}

export function getSubmitButton(evt) {
  const submitter = evt?.submitter;
  if (isLoadingButton(submitter)) return submitter;

  const active = document.activeElement;
  if (isLoadingButton(active)) return active;

  const form = evt?.target;
  if (form instanceof HTMLFormElement) {
    const fallback = form.querySelector('button.ar-btn[type="submit"], button.ar-btn:not([type])');
    if (isLoadingButton(fallback)) return fallback;
  }

  return null;
}

// Top-of-viewport navigation progress bar. start() arms a short timer and only
// reveals the bar if the route change is still running after ~120ms, so the
// instant cache-backed renders (invoice/contacts/schedule lists) never flash
// it — the bar only appears for screens that wait on an API round-trip.
let navProgressTimer = null;
let navProgressVisible = false;

export function startNavProgress() {
  const bar = document.getElementById("nav-progress");
  if (!bar) return;
  window.clearTimeout(navProgressTimer);
  navProgressTimer = window.setTimeout(() => {
    navProgressVisible = true;
    bar.classList.add("is-active");
  }, 120);
}

export function finishNavProgress() {
  const bar = document.getElementById("nav-progress");
  window.clearTimeout(navProgressTimer);
  if (!bar) return;
  if (navProgressVisible) bar.classList.remove("is-active");
  navProgressVisible = false;
}

// Resolve on the next animation frame, but never hang: requestAnimationFrame is
// paused in backgrounded tabs, prerender, some webviews and headless/preview
// runtimes, so race it against a short timeout. Without the fallback, the two
// awaits below would never settle there and the action (e.g. a form save) would
// never run — the button would just spin forever.
function nextPaint() {
  return new Promise((resolve) => {
    let settled = false;
    const done = () => { if (!settled) { settled = true; resolve(); } };
    window.requestAnimationFrame(done);
    window.setTimeout(done, 50);
  });
}

export async function withButtonLoading(button, action) {
  if (!isLoadingButton(button)) return action();
  const wasDisabled = button.disabled;
  button.classList.add("is-loading");
  button.setAttribute("aria-busy", "true");
  button.disabled = true;
  try {
    // Two frames so the loading style paints before the action runs.
    await nextPaint();
    await nextPaint();
    return await action();
  } finally {
    button.classList.remove("is-loading");
    button.removeAttribute("aria-busy");
    button.disabled = wasDisabled;
  }
}

// Treat an href as an in-app SPA target if it's either a hash link (#/foo,
// used on the demo page) or a path link (/foo, used everywhere else). Returns
// the normalized path or null if the link should be left alone (mailto, tel,
// external, same-page anchor, etc.).
export function hrefToAppPath(href) {
  const value = String(href || "");
  if (!value) return null;
  if (value.startsWith("#/")) return value.slice(1);
  if (value.startsWith("/") && !value.startsWith("//")) return value;
  return null;
}

export function autoGrowTextarea(textarea) {
  if (!(textarea instanceof HTMLTextAreaElement)) return;
  textarea.style.height = "auto";
  const minHeight = Number.parseFloat(window.getComputedStyle(textarea).minHeight) || 72;
  textarea.style.height = `${Math.max(minHeight, textarea.scrollHeight)}px`;
}

export function wireAutoGrowTextareas(root = document) {
  const textareas = root.querySelectorAll("textarea.ar-textarea-spec-control");
  textareas.forEach((textarea) => {
    if (textarea.dataset.autogrowBound !== "true") {
      textarea.addEventListener("input", () => autoGrowTextarea(textarea));
      textarea.dataset.autogrowBound = "true";
    }
    autoGrowTextarea(textarea);
  });
}

// Give a topbar a divider once its content has actually scrolled up against
// it. Wired ONCE, globally: `scroll` doesn't bubble, but it does fire during
// the capture phase, so one document-level listener covers every surface built
// on one of the chromes below — invoice and quote compose, the edit screens,
// the email editors, and every drawer — including any added later, with no
// per-render wiring to forget.
//
// The topbar is matched as a direct sibling of the scroller, so a surface that
// reuses one of these scrollers without a topbar simply no-ops.
const TOPBAR_DIVIDERS = [
  { scroller: "invoice-form-content", topbar: ".invoice-form-topbar" },
  { scroller: "ar-drawer-content", topbar: ".ar-drawer-topbar" }
];

// How far the form can scroll before anything visually reaches the top edge.
// The form carries its own top padding (32px today), so the first scrolled
// pixels only eat that whitespace — showing the divider at scrollTop > 0 put a
// rule there while there was still a clear gap and nothing to divide. Measured
// live rather than hard-coded so it stays correct if that padding changes.
function composeLeadingGap(scroller) {
  const form = scroller.firstElementChild;
  if (!form) return 0;
  const cs = getComputedStyle(form);
  return (Number.parseFloat(cs.paddingTop) || 0) + (Number.parseFloat(cs.marginTop) || 0);
}

export function wireTopbarDividers() {
  document.addEventListener(
    "scroll",
    (evt) => {
      const scroller = evt.target;
      if (!(scroller instanceof Element)) return;
      const kind = TOPBAR_DIVIDERS.find((d) => scroller.classList.contains(d.scroller));
      if (!kind) return;
      const topbar = scroller.parentElement?.querySelector(`:scope > ${kind.topbar}`);
      if (!topbar) return;
      // Browsers coalesce scroll events to at most one per frame, so this reads
      // layout no more often than a rAF throttle would.
      topbar.classList.toggle("is-scrolled", scroller.scrollTop > composeLeadingGap(scroller));
    },
    true
  );
}
